import { ButtonHTMLAttributes, ForwardedRef, ReactNode, forwardRef } from "react";
import { Slot } from "radix-ui";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import { Spinner } from "app/icons/navigation/Spinner";
import styles from "./Button.module.scss";

export type ButtonVariant = "primary" | "secondary" | "borderless";
export type ButtonSize = "sm" | "md" | "lg" | "icon-sm" | "icon-md" | "icon-lg";

const VARIANT_CLASSNAMES = {
	primary: styles["button--primary"],
	secondary: styles["button--secondary"],
	borderless: styles["button--borderless"],
};

const SIZE_CLASSNAMES = {
	sm: styles["button--sm"],
	md: styles["button--md"],
	lg: styles["button--lg"],
	"icon-sm": styles["button--icon-sm"],
	"icon-md": styles["button--icon-md"],
	"icon-lg": styles["button--icon-lg"],
};

type ButtonBaseProps = {
	variant?: ButtonVariant;
	size?: ButtonSize;
	/** Icon before the label — sized by the button. */
	startIcon?: ReactNode;
	/** Icon after the label — sized by the button. */
	endIcon?: ReactNode;
	fullWidth?: boolean;
};

type ButtonOwnProps =
	| (ButtonBaseProps & {
			/** Merges the button styling onto its child element instead of rendering one. */
			asChild: true;
			/** Not available with `asChild`: a spinner can't be rendered into an unknown child — handle loading inside your child. */
			loading?: never;
	  })
	| (ButtonBaseProps & {
			asChild?: false;
			/** Shows a spinner and disables the button while an action is in progress. */
			loading?: boolean;
	  });

export type ButtonProps = ButtonOwnProps &
	Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps | "loading" | "asChild">;

/**
 * Design System button.
 *
 * Renders a native `<button>`, or merges its styling onto your own element with
 * `asChild` (e.g. a link, to build a button-like link). `startIcon`/`endIcon`
 * are sized by the button; icons passed as `children` keep their own size. The
 * `icon-*` sizes render a square icon-only button and expect an accessible label.
 */
export const Button = forwardRef(
	(
		{
			variant = "primary",
			size = "md",
			startIcon,
			endIcon,
			fullWidth = false,
			asChild = false,
			loading = false,
			disabled = false,
			children,
			className,
			...rest
		}: ButtonProps,
		ref: ForwardedRef<HTMLButtonElement | HTMLAnchorElement>
	) => {
		const buttonClassName = classNames(
			styles.button,
			VARIANT_CLASSNAMES[variant],
			SIZE_CLASSNAMES[size],
			{
				[styles["button--full-width"]]: fullWidth,
				[styles["button--has-start-icon"]]: Boolean(startIcon),
				[styles["button--has-end-icon"]]: Boolean(endIcon),
			},
			className
		);

		const isIconOnly = size.startsWith("icon-");

		const start = startIcon && (
			<span className={styles["button__icon"]} aria-hidden="true">
				{startIcon}
			</span>
		);

		const end = endIcon && (
			<span className={styles["button__icon"]} aria-hidden="true">
				{endIcon}
			</span>
		);

		if (asChild) {
			return (
				<Slot.Root
					{...rest}
					ref={ref}
					className={buttonClassName}
					aria-disabled={disabled || undefined}
				>
					{start}
					<Slot.Slottable>{children}</Slot.Slottable>
					{end}
				</Slot.Root>
			);
		}

		return (
			<button
				type="button"
				{...rest}
				ref={ref as ForwardedRef<HTMLButtonElement>}
				className={buttonClassName}
				disabled={disabled || loading}
				aria-busy={loading || undefined}
			>
				{loading ? (
					<>
						<span className={styles["button__icon"]}>
							<Spinner />
						</span>
						{!isIconOnly && <FormattedMessage id="general.button.loading.label" />}
					</>
				) : (
					<>
						{start}
						{children}
						{end}
					</>
				)}
			</button>
		);
	}
);

Button.displayName = "Button";

export default Button;
